import { useState, useEffect } from 'react';
import { Link } from 'react-router';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface AllocationSnapshot {
  date: string;
  totalRevenueSol: number;
  prizePoolSol: number;
  opsSol: number;
  buyBurnSol: number;
}

interface TransparencyData {
  currentSeason: {
    id: number;
    name: string;
    prizePoolSol: number;
    totalRevenueSol: number;
  };
  revenueSplit: {
    prizePool: number;
    operations: number;
    buyBurn: number;
  };
  dailySnapshots: AllocationSnapshot[];
  stats: {
    totalPlayers: number;
    totalGames: number;
    totalCreditsPurchased: number;
  };
  revenueSources: {
    creditSalesSol: number;
    devRewardsSol: number;
    totalRevenueSol: number;
  };
  solPriceUsd: number | null;
}

export default function Transparency() {
  const [data, setData] = useState<TransparencyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(`${API_BASE}/api/transparency`);
        const result = await response.json();
        if (result.success) {
          setData(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch transparency data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  const formatSol = (amount: number) => amount.toFixed(2);
  const formatUsd = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(2)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const solToUsd = (sol: number) => {
    if (!data?.solPriceUsd) return null;
    return sol * data.solPriceUsd;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Subtle gradient mesh */}
        <div className="absolute inset-0 opacity-30" style={{
          background: `
            radial-gradient(ellipse 60% 40% at 20% 20%, rgba(34, 197, 94, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse 50% 30% at 80% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)
          `
        }} />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16 animate-slide-up-fade">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full mb-6">
            <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-white/50">Live Data</span>
            {data?.solPriceUsd && (
              <>
                <span className="w-px h-3 bg-white/10" />
                <span className="text-[11px] font-mono text-white/40">
                  SOL ${data.solPriceUsd.toFixed(2)}
                </span>
              </>
            )}
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-[0.02em] mb-4">
            TRANSPARENCY
          </h1>
          <p className="text-white/40 text-xl max-w-xl mx-auto leading-relaxed">
            Full visibility into revenue allocation. Every SOL tracked and distributed.
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-20 animate-slide-up-fade">
            <div className="w-12 h-12 mx-auto mb-4 border-2 border-white/10 border-t-[#22c55e] rounded-full animate-spin" />
            <p className="text-white/30 tracking-[0.2em] text-sm uppercase">Loading</p>
          </div>
        ) : data ? (
          <>
            {/* Total Value Hero */}
            <div className="mb-8 animate-slide-up-fade" style={{ animationDelay: '0.05s' }}>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.06]">
                {/* Decorative corner elements */}
                <div className="absolute top-0 left-0 w-32 h-32 border-l-2 border-t-2 border-[#22c55e]/20 rounded-tl-2xl" />
                <div className="absolute bottom-0 right-0 w-32 h-32 border-r-2 border-b-2 border-[#22c55e]/20 rounded-br-2xl" />

                <div className="relative p-8 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    {/* Prize Pool */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-white/40 text-xs tracking-[0.25em] uppercase">
                          Prize Pool
                        </div>
                        {/* Growing indicator */}
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/20">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22c55e]"></span>
                          </span>
                          <span className="text-[10px] tracking-[0.15em] uppercase text-[#22c55e]/80 font-medium">Growing</span>
                          <svg className="w-2.5 h-2.5 text-[#22c55e]/60 animate-bounce" style={{ animationDuration: '2s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-4 mb-2">
                        <span className="font-display text-6xl md:text-7xl text-[#22c55e] tracking-tight">
                          {formatSol(data.currentSeason.prizePoolSol)}
                        </span>
                        <span className="text-2xl text-white/30 font-light">SOL</span>
                      </div>
                      {data.solPriceUsd && (
                        <div className="font-mono text-xl text-white/50">
                          {formatUsd(data.currentSeason.prizePoolSol * data.solPriceUsd)}
                          <span className="text-white/30 text-sm ml-2">USD</span>
                        </div>
                      )}
                      <div className="mt-3 text-sm text-white/30 tracking-wide">
                        Increases with every credit purchased until finals conclude
                      </div>
                    </div>

                    {/* Split Visualization */}
                    <div className="flex items-center justify-center md:justify-end">
                      <div className="relative">
                        <svg className="w-40 h-40" viewBox="0 0 100 100">
                          {/* Background circle */}
                          <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                          {/* Prize Pool arc */}
                          <circle
                            cx="50" cy="50" r="42" fill="none"
                            stroke="#22c55e" strokeWidth="8"
                            strokeDasharray={`${data.revenueSplit.prizePool * 2.64} 264`}
                            strokeDashoffset="66"
                            strokeLinecap="round"
                            className="drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]"
                          />
                          {/* Operations arc */}
                          <circle
                            cx="50" cy="50" r="42" fill="none"
                            stroke="#3b82f6" strokeWidth="8"
                            strokeDasharray={`${data.revenueSplit.operations * 2.64} 264`}
                            strokeDashoffset={`${66 - data.revenueSplit.prizePool * 2.64}`}
                            strokeLinecap="round"
                          />
                          {/* Buy & Burn arc */}
                          <circle
                            cx="50" cy="50" r="42" fill="none"
                            stroke="#f59e0b" strokeWidth="8"
                            strokeDasharray={`${data.revenueSplit.buyBurn * 2.64} 264`}
                            strokeDashoffset={`${66 - (data.revenueSplit.prizePool + data.revenueSplit.operations) * 2.64}`}
                            strokeLinecap="round"
                          />
                          {/* Center text */}
                          <text x="50" y="46" textAnchor="middle" className="fill-white/60 text-[8px] font-mono tracking-wider">TOTAL</text>
                          <text x="50" y="58" textAnchor="middle" className="fill-white text-sm font-bold">{formatSol(data.currentSeason.totalRevenueSol)}</text>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Sources */}
            <div className="grid md:grid-cols-2 gap-4 mb-8 animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
              {/* Credit Sales */}
              <div className="group relative overflow-hidden rounded-xl bg-white/[0.02] border border-white/[0.06] p-6 hover:border-white/[0.12] transition-all duration-300">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#3b82f6]/10 to-transparent rounded-bl-full" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#3b82f6]/10 border border-[#3b82f6]/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm tracking-[0.2em] uppercase text-white/40">Credit Sales</div>
                      <div className="text-xs text-white/30">Run packs purchased</div>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-3xl text-white">{formatSol(data.revenueSources.creditSalesSol)}</span>
                    <span className="text-white/30">SOL</span>
                  </div>
                  {data.solPriceUsd && (
                    <div className="font-mono text-sm text-white/40 mt-1">
                      {formatUsd(solToUsd(data.revenueSources.creditSalesSol) || 0)}
                    </div>
                  )}
                </div>
              </div>

              {/* Dev Rewards */}
              <div className="group relative overflow-hidden rounded-xl bg-white/[0.02] border border-white/[0.06] p-6 hover:border-white/[0.12] transition-all duration-300">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#a855f7]/10 to-transparent rounded-bl-full" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#a855f7]/10 border border-[#a855f7]/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#a855f7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm tracking-[0.2em] uppercase text-white/40">Dev Rewards</div>
                      <div className="text-xs text-white/30">Creator fees from pump.fun</div>
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-3xl text-white">{formatSol(data.revenueSources.devRewardsSol)}</span>
                    <span className="text-white/30">SOL</span>
                  </div>
                  {data.solPriceUsd && (
                    <div className="font-mono text-sm text-white/40 mt-1">
                      {formatUsd(solToUsd(data.revenueSources.devRewardsSol) || 0)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Combined Total Bar */}
            <div className="mb-10 animate-slide-up-fade" style={{ animationDelay: '0.15s' }}>
              <div className="rounded-xl bg-gradient-to-r from-white/[0.03] to-white/[0.01] border border-white/[0.06] p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="text-sm tracking-[0.2em] uppercase text-white/40 mb-1">Combined Revenue</div>
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-4xl font-bold text-white">{formatSol(data.revenueSources.totalRevenueSol)}</span>
                      <span className="text-white/30 text-lg">SOL</span>
                      {data.solPriceUsd && (
                        <span className="font-mono text-xl text-[#22c55e]">
                          ({formatUsd(solToUsd(data.revenueSources.totalRevenueSol) || 0)})
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Visual breakdown bar */}
                  <div className="flex-1 max-w-md">
                    <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
                      <div
                        className="bg-[#3b82f6] transition-all duration-500"
                        style={{ width: `${(data.revenueSources.creditSalesSol / data.revenueSources.totalRevenueSol) * 100}%` }}
                      />
                      <div
                        className="bg-[#a855f7] transition-all duration-500"
                        style={{ width: `${(data.revenueSources.devRewardsSol / data.revenueSources.totalRevenueSol) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-white/40">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#3b82f6]" />
                        Credits {((data.revenueSources.creditSalesSol / data.revenueSources.totalRevenueSol) * 100).toFixed(1)}%
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-[#a855f7]" />
                        Dev Rewards {((data.revenueSources.devRewardsSol / data.revenueSources.totalRevenueSol) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Split + Stats */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Revenue Allocation */}
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-6 animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
                <div className="text-white/40 text-sm tracking-[0.2em] uppercase mb-6">
                  Allocation Split
                </div>

                <div className="space-y-5">
                  {/* Prize Pool */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
                        <span className="text-base text-white/70">Prize Pool</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-white/50">
                          {formatSol(data.currentSeason.totalRevenueSol * data.revenueSplit.prizePool / 100)} SOL
                        </span>
                        <span className="font-mono text-base text-[#22c55e] w-14 text-right">{data.revenueSplit.prizePool}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#22c55e] rounded-full transition-all duration-700" style={{ width: `${data.revenueSplit.prizePool}%` }} />
                    </div>
                  </div>

                  {/* Dev */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#3b82f6]" />
                        <span className="text-base text-white/70">Dev</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-white/50">
                          {formatSol(data.currentSeason.totalRevenueSol * data.revenueSplit.operations / 100)} SOL
                        </span>
                        <span className="font-mono text-base text-[#3b82f6] w-14 text-right">{data.revenueSplit.operations}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#3b82f6] rounded-full transition-all duration-700" style={{ width: `${data.revenueSplit.operations}%` }} />
                    </div>
                  </div>

                  {/* Operations & S2 */}
                  <div>
                    <div className="flex justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b]" />
                        <span className="text-base text-white/70">Operations & S2</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-white/50">
                          {formatSol(data.currentSeason.totalRevenueSol * data.revenueSplit.buyBurn / 100)} SOL
                        </span>
                        <span className="font-mono text-base text-[#f59e0b] w-14 text-right">{data.revenueSplit.buyBurn}%</span>
                      </div>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-[#f59e0b] rounded-full transition-all duration-700" style={{ width: `${data.revenueSplit.buyBurn}%` }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-6 animate-slide-up-fade" style={{ animationDelay: '0.25s' }}>
                <div className="text-white/40 text-sm tracking-[0.2em] uppercase mb-6">
                  Season Stats
                </div>

                <div className="space-y-5">
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-white/50 text-base">Total Players</span>
                    <span className="font-mono text-2xl font-bold">{data.stats.totalPlayers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/5">
                    <span className="text-white/50 text-base">Games Played</span>
                    <span className="font-mono text-2xl font-bold">{data.stats.totalGames.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-white/50 text-base">Credits Purchased</span>
                    <span className="font-mono text-2xl font-bold">{data.stats.totalCreditsPurchased.toLocaleString()}</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-white/5">
                  <Link
                    to="/leaderboard"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-white/10 rounded-lg text-white/60 text-sm tracking-wide hover:border-[#22c55e]/50 hover:text-[#22c55e] transition-all"
                  >
                    View Leaderboard
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Daily Snapshots */}
            <div className="animate-slide-up-fade" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="text-white/40 text-sm tracking-[0.2em] uppercase">
                  Daily Allocations
                </div>
                <div className="text-xs text-white/30">Credit sales only</div>
              </div>

              <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
                {/* Table header */}
                <div className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-white/5 text-xs text-white/40 tracking-[0.15em] uppercase">
                  <div>Date</div>
                  <div className="text-right">Revenue</div>
                  <div className="text-right">Prize Pool</div>
                  <div className="text-right">Dev</div>
                  <div className="text-right">Ops & S2</div>
                </div>

                {/* Table body */}
                <div className="divide-y divide-white/5">
                  {data.dailySnapshots.length === 0 ? (
                    <div className="px-6 py-12 text-center text-white/30">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-full border border-white/10 flex items-center justify-center">
                        <svg className="w-6 h-6 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      No allocations recorded yet
                    </div>
                  ) : (
                    data.dailySnapshots.map((snapshot, index) => (
                      <div
                        key={snapshot.date}
                        className="grid grid-cols-5 gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors"
                        style={{ animationDelay: `${0.3 + index * 0.02}s` }}
                      >
                        <div className="text-white/60 font-medium">{formatDate(snapshot.date)}</div>
                        <div className="text-right font-mono text-white/80">
                          {formatSol(snapshot.totalRevenueSol)}
                        </div>
                        <div className="text-right font-mono text-[#22c55e]">
                          +{formatSol(snapshot.prizePoolSol)}
                        </div>
                        <div className="text-right font-mono text-[#3b82f6]">
                          {formatSol(snapshot.opsSol)}
                        </div>
                        <div className="text-right font-mono text-[#f59e0b]">
                          {formatSol(snapshot.buyBurnSol)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer note */}
            <div className="mt-12 text-center animate-slide-up-fade" style={{ animationDelay: '0.35s' }}>
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-white/[0.02] rounded-full border border-white/[0.04]">
                <svg className="w-5 h-5 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-white/40 text-base">
                  All allocations calculated automatically and verifiable on-chain
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-20 rounded-xl bg-white/[0.02] border border-white/[0.06]">
            <p className="text-white/40">Failed to load transparency data</p>
          </div>
        )}
      </div>
    </div>
  );
}
